import React, { useMemo, useRef, useState } from 'react';
import styles from './MasterImportModal.module.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';


function previewCell(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') return value.label || '—';
  return String(value);
}

const EMPTY_STATE = {
  step: 1,
  parseId: null,
  fileName: '',
  headers: [],
  rowCount: 0,
  columns: [],
  mapping: {},
  filterColumn: '',
  filterValue: '',
  filterOptions: [],
  dedupeFields: [],
  summary: null,
  previewRows: [],
  errors: [],
};

function MasterImportModal({ template, getAuthHeaders, onClose, onImported }) {
  const fileInputRef = useRef(null);
  const [state, setState] = useState(EMPTY_STATE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const filterValues = useMemo(() => {
    if (!state.filterColumn) return [];
    const option = state.filterOptions.find((item) => item.column === state.filterColumn);
    return option?.values || [];
  }, [state.filterColumn, state.filterOptions]);

  const mappedCount = Object.values(state.mapping).filter(Boolean).length;

  const close = () => {
    if (busy) return;
    onClose?.();
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (!['.csv', '.xlsx', '.xls'].includes(ext)) {
      setError('Please upload a CSV or Excel file (.csv, .xlsx, .xls).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File size exceeds 10MB limit.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/schemas/${template.id}/import/parse`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
        },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to parse file');
      }

      setState({
        ...EMPTY_STATE,
        step: 1,
        parseId: result.parse_id,
        fileName: result.file_name || file.name,
        headers: result.headers || [],
        rowCount: result.row_count || 0,
        columns: result.columns || [],
        mapping: result.suggested_mapping || {},
        filterOptions: result.filter_options || [],
        dedupeFields: result.dedupe_fields || [],
      });
    } catch (err) {
      setError(err.message || 'Failed to parse file');
      setState(EMPTY_STATE);
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const updateMapping = (header, fieldName) => {
    setState((prev) => ({
      ...prev,
      mapping: {
        ...prev.mapping,
        [header]: fieldName || null,
      },
      summary: null,
      previewRows: [],
    }));
  };

  const runPreview = async () => {
    if (!state.parseId) return;
    if (mappedCount === 0) {
      setError('Map at least one column to a field.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/schemas/${template.id}/import/preview`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parse_id: state.parseId,
          column_mapping: state.mapping,
          filter:
            state.filterColumn && state.filterValue
              ? { column: state.filterColumn, value: state.filterValue }
              : null,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to build preview');
      }

      setState((prev) => ({
        ...prev,
        step: 3,
        summary: result.summary,
        previewRows: result.preview_rows || [],
        dedupeFields: result.dedupe_fields || prev.dedupeFields,
        errors: result.errors || [],
      }));
    } catch (err) {
      setError(err.message || 'Failed to build preview');
    } finally {
      setBusy(false);
    }
  };

  const confirmImport = async () => {
    if (!state.parseId) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/schemas/${template.id}/import/confirm`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parse_id: state.parseId,
          column_mapping: state.mapping,
          filter:
            state.filterColumn && state.filterValue
              ? { column: state.filterColumn, value: state.filterValue }
              : null,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to import records');
      }
      onImported?.(result);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Failed to import records');
    } finally {
      setBusy(false);
    }
  };

  const goNext = () => {
    if (state.step === 1) {
      if (!state.parseId) {
        setError('Upload a file first.');
        return;
      }
      setError('');
      setState((prev) => ({ ...prev, step: 2 }));
      return;
    }
    if (state.step === 2) {
      runPreview();
      return;
    }
    if (state.step === 3) {
      confirmImport();
    }
  };

  const goBack = () => {
    if (busy) return;
    setError('');
    setState((prev) => ({
      ...prev,
      step: Math.max(1, prev.step - 1),
    }));
  };

  const stepClass = (n) => {
    if (state.step === n) return `${styles.stepDot} ${styles.stepActive}`;
    if (state.step > n) return `${styles.stepDot} ${styles.stepDone}`;
    return styles.stepDot;
  };

  const nextDisabled =
    busy ||
    (state.step === 1 && !state.parseId) ||
    (state.step === 2 && mappedCount === 0) ||
    (state.step === 3 && !(state.summary?.new_rows > 0));

  return (
    <div className={styles.overlay} onClick={close}>
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h3>Import into {template.name}</h3>
            <p className={styles.sub}>Upload, map columns, then preview before inserting.</p>
          </div>
          <button type="button" className={styles.closeBtn} onClick={close} disabled={busy}>
            ×
          </button>
        </div>

        <div className={styles.steps}>
          <div className={stepClass(1)}>
            <span className={styles.dot}>1</span>Upload
          </div>
          <div className={styles.stepLine} />
          <div className={stepClass(2)}>
            <span className={styles.dot}>2</span>Map &amp; filter
          </div>
          <div className={styles.stepLine} />
          <div className={stepClass(3)}>
            <span className={styles.dot}>3</span>Preview
          </div>
        </div>

        <div className={styles.body}>
          {state.step === 1 && (
            <div>
              <label className={styles.label}>CSV or Excel file</label>
              <button
                type="button"
                className={styles.dropzone}
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                <p className={styles.dropTitle}>
                  {busy ? 'Parsing file…' : 'Click to choose a file'}
                </p>
                <p className={styles.dropHint}>First row should be column headers · Max 10MB</p>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleFile}
              />
              {state.fileName && (
                <p className={styles.fileMeta}>
                  {state.fileName} · {state.rowCount} row{state.rowCount === 1 ? '' : 's'}
                </p>
              )}
            </div>
          )}

          {state.step === 2 && (
            <div>
              <label className={styles.label}>Map columns from your file to fields</label>
              <div className={styles.mappingList}>
                {state.headers.map((header) => (
                  <div key={header} className={styles.mapRow}>
                    <span className={styles.mapHeader}>{header}</span>
                    <span className={styles.mapArrow}>→</span>
                    <select
                      value={state.mapping[header] || ''}
                      onChange={(e) => updateMapping(header, e.target.value)}
                      className={styles.select}
                    >
                      <option value="">Don&apos;t import</option>
                      {state.columns.map((col) => (
                        <option key={col.name} value={col.name}>
                          {col.name}
                          {col.required ? ' *' : ''}
                          {col.type === 'reference' ? ' (master)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              {state.columns.some((col) => col.type === 'reference') && (
                <p className={styles.hint}>
                  Fields marked (master) must match a value on the linked master table, such as a
                  name or id.
                </p>
              )}

              <label className={styles.label}>Only import rows where</label>
              <div className={styles.filterRow}>
                <select
                  value={state.filterColumn}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      filterColumn: e.target.value,
                      filterValue: '',
                      summary: null,
                    }))
                  }
                  className={styles.select}
                >
                  <option value="">Any</option>
                  {state.headers.map((header) => (
                    <option key={header} value={header}>
                      {header}
                    </option>
                  ))}
                </select>
                <span className={styles.equals}>equals</span>
                <select
                  value={state.filterValue}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      filterValue: e.target.value,
                      summary: null,
                    }))
                  }
                  className={styles.select}
                  disabled={!state.filterColumn}
                >
                  <option value="">Any</option>
                  {filterValues.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
              <p className={styles.hint}>
                Useful when one file covers multiple classes or sections — leave as Any to import
                everything.
              </p>
            </div>
          )}

          {state.step === 3 && (
            <div>
              <p className={styles.summary}>
                {state.summary?.after_filter === 0
                  ? 'No rows matched this filter.'
                  : `${state.summary?.after_filter || 0} row${
                      state.summary?.after_filter === 1 ? '' : 's'
                    } matched · ${state.summary?.new_rows || 0} new · ${
                      state.summary?.duplicate_rows || 0
                    } already in ${template.name} and will be skipped. Duplicate check uses: ${(
                      state.dedupeFields || []
                    ).join(' + ') || 'first field'}.`}
              </p>

              {state.previewRows?.length > 0 && (
                <div className={styles.previewScroll}>
                  <table className={styles.previewTable}>
                    <thead>
                      <tr>
                        {state.columns.map((col) => (
                          <th key={col.name}>{col.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {state.previewRows.map((row, idx) => (
                        <tr key={idx}>
                          {state.columns.map((col) => (
                            <td key={col.name}>{previewCell(row[col.name])}</td>
                          ))}
                        </tr>
                      ))}
                      {(state.summary?.new_rows || 0) > state.previewRows.length && (
                        <tr>
                          <td colSpan={state.columns.length} className={styles.moreRows}>
                            + {state.summary.new_rows - state.previewRows.length} more row(s)
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {state.errors?.length > 0 && (
                <div className={styles.errorBox}>
                  <strong>Validation issues</strong>
                  <ul>
                    {state.errors.slice(0, 5).map((item, i) => (
                      <li key={i}>
                        Row {item.row}: {(item.errors || []).join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {error && <div className={styles.errorMessage}>{error}</div>}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.btn}
            onClick={goBack}
            disabled={busy || state.step === 1}
            style={{ visibility: state.step === 1 ? 'hidden' : 'visible' }}
          >
            Back
          </button>
          <div className={styles.footerRight}>
            <button type="button" className={styles.btn} onClick={close} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnAccent}`}
              onClick={goNext}
              disabled={nextDisabled}
            >
              {busy
                ? 'Working…'
                : state.step === 3
                  ? 'Import records'
                  : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MasterImportModal;
